import { adminRequest } from './adminApi'
import { API_BASE, ApiError, request } from './http'

export interface ModelBackendMeta {
  category: string
  backend_name: string
  has_api_key: boolean
  db_overridden: boolean
}

export type ModelBackendConfig = Record<string, unknown>

export type ModelsResponse = Record<string, unknown>

export interface PutModelCategoryBody {
  backend_name: string
  config: ModelBackendConfig
  api_key?: string
}

export interface PutModelCategoryResponse {
  saved: boolean
  hot_reloaded: boolean
  message: string
}

export interface TestModelBackendBody {
  backend_name?: string
  base_url?: string
  api_key?: string
}

export interface TestModelBackendResponse {
  ok: boolean
  unsupported?: boolean
  error?: string
}

export interface ModelBackendHealthItem {
  category: string
  backend_name: string
  disabled: boolean
  ok: boolean | null
  checked_at: number
  unsupported?: boolean
  error?: string
}

export interface ModelBackendHealthResponse {
  items: ModelBackendHealthItem[]
  summary: {
    ok: number
    failed: number
    unknown: number
    disabled: number
  }
  tts_fallback?: {
    configured: boolean
    primary_failures: number
    fallback_served: number
  }
}

export interface DiscoverModelsResponse {
  models: string[]
  message?: string
  error?: string
}

export interface TranscribeStatusResponse {
  available: boolean
  model?: string
  languages?: string[]
}

export interface TranscribeResponse {
  text: string
}

export function getModels(): Promise<ModelsResponse> {
  return adminRequest<ModelsResponse>('/v1/admin/models')
}

export function putCategory(
  category: string,
  body: PutModelCategoryBody,
): Promise<PutModelCategoryResponse> {
  return adminRequest<PutModelCategoryResponse>(`/v1/admin/models/${encodeURIComponent(category)}`, {
    method: 'PUT',
    body,
  })
}

export function testBackend(
  category: string,
  body: TestModelBackendBody,
): Promise<TestModelBackendResponse> {
  return probeModelBackend(category, body)
}

export function probeModelBackend(
  category: string,
  body: TestModelBackendBody,
): Promise<TestModelBackendResponse> {
  return adminRequest<TestModelBackendResponse>(`/v1/admin/models/${encodeURIComponent(category)}/test`, {
    method: 'POST',
    body,
  })
}

export function getModelBackendHealth(): Promise<ModelBackendHealthResponse> {
  return adminRequest<ModelBackendHealthResponse>('/v1/admin/models/health')
}

export function discoverModels(
  category: string,
  backendName?: string,
): Promise<DiscoverModelsResponse> {
  const params = new URLSearchParams()
  if (backendName) params.set('backend_name', backendName)
  const query = params.toString()
  return adminRequest<DiscoverModelsResponse>(
    `/v1/admin/models/${encodeURIComponent(category)}/discover${query ? `?${query}` : ''}`,
  )
}

export function getTranscribeStatus(): Promise<TranscribeStatusResponse> {
  return request<TranscribeStatusResponse>('/v1/transcribe/status')
}

export async function transcribeAudio(
  audio: Blob,
  language?: string,
  signal?: AbortSignal,
): Promise<TranscribeResponse> {
  const form = new FormData()
  form.append('audio', audio)
  if (language) form.append('language', language)

  const res = await fetch(`${API_BASE}/v1/transcribe`, {
    method: 'POST',
    credentials: 'include',
    body: form,
    signal,
  })

  const text = await res.text()
  let data: unknown = undefined
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }
  }

  if (!res.ok) {
    let message = res.statusText
    let code: string | undefined
    if (data && typeof data === 'object') {
      const payload = data as Record<string, unknown>
      if (typeof payload.error === 'string') message = payload.error
      else if (typeof payload.message === 'string') message = payload.message
      if (typeof payload.code === 'string') code = payload.code
    }
    throw new ApiError(res.status, message, code)
  }

  return data as TranscribeResponse
}
